import {
  Collateral,
  CollateralBasket,
  EntityStatus,
  EntityType,
  MarketType,
  MasterLegalAgreementType,
  OrganisationType,
  PlatformFeature,
  TradingEntity,
  TradingEntityType,
  TradingRelationship
} from 'api'
import { isEmpty, isNil, uniq } from 'lodash'
import { canTrade, canTradeSector, doesBasketTypeRequiresTradingEntityPermission, hasTradingRelationship } from 'model'
import { Selector, createSelector } from '@reduxjs/toolkit'
import { isNotNil } from 'utils/predicate'
import { createAuthorisedUserSelector } from '../connectionSelectors'
import { createAllEntitiesSelector, createEntityGetterSelector } from '../entitySelectors'

/**
 * Trading Entity filter functions / factories / selectors
 */
export const isTradingEntityInAuthorisedUserOrganisationSelector = createSelector(
  [createAuthorisedUserSelector()],
  authorisedUser => (tradingEntity: TradingEntity) => tradingEntity.organisationId === authorisedUser.organisationId
)

export const isTradingEntityAssignedToAuthorisedUserSelector = createSelector(
  [createAuthorisedUserSelector()],
  authorisedUser => (tradingEntity: TradingEntity) => authorisedUser.tradingEntityDetails.some(d => d.tradingEntityId === tradingEntity.id)
)

export const createTradingEntityHasBuySideRelationshipWithFundsSelector = createSelector(
  [createAllEntitiesSelector(EntityType.TRADING_RELATIONSHIP)],
  tradingRelationships => (counterpartyIds: string[]) => (tradingEntity: TradingEntity) =>
    isEmpty(counterpartyIds)
      ? true
      : counterpartyIds.every(id => hasTradingRelationship(tradingEntity.id, id, tradingRelationships, false, []))
)
export const createTradingEntityHasSellSideRelationshipWithFunds = (
  fundIds: string[],

  allTradingRelationships: TradingRelationship[],
  usSponsoredClearing: boolean,
  masterLegalAgreementTypes: MasterLegalAgreementType[]
) => (tradingEntity: TradingEntity) =>
  isEmpty(fundIds) ||
  fundIds.some(
    fundId => !hasTradingRelationship(fundId, tradingEntity.id, allTradingRelationships, usSponsoredClearing, masterLegalAgreementTypes)
  )
    ? false
    : true

export const isTradingEntityTradable = (tradingEntity: TradingEntity) => canTrade(tradingEntity.productRights)

export const tradingEntityIsStandardType = (tradingEntity: TradingEntity) =>
  tradingEntity.tradingEntityTypes.includes(TradingEntityType.STANDARD)

export const createTradingEntityCanTradeCollaterals = <
  T extends {
    collateral: Collateral
    marketType: MarketType | null
  }
>(
  details: T[]
) => (tradingEntity: TradingEntity) => {
  if (isEmpty(details)) {
    return true
  }

  return details.every(({ collateral, marketType }) => {
    return canTradeSector(collateral.productSectorId, tradingEntity.productRights, marketType)
  })
}

export const createTradingEntityCanTradeCollateralBasketsSelector = createSelector(
  [createEntityGetterSelector(EntityType.PRODUCT_SECTOR)],
  getProductSector => <T extends { collateralBasket: CollateralBasket; marketType: MarketType | null }>(details: T[]) => (
    tradingEntity: TradingEntity
  ) => {
    return isEmpty(details)
      ? true
      : details.every(({ collateralBasket, marketType }) => {
          const productSector = getProductSector(collateralBasket.productSectorId)
          const doesRequirePermission =
            productSector?.basketType && doesBasketTypeRequiresTradingEntityPermission(productSector?.basketType)

          return (
            canTradeSector(collateralBasket.productSectorId, tradingEntity.productRights, marketType) &&
            (!doesRequirePermission || collateralBasket.tradingEntityIds.includes(tradingEntity.id))
          )
        })
  }
)

export const tradingEntityHasRepoTypeSBLGetterSelector = createSelector(
  [createEntityGetterSelector(EntityType.ORGANISATION)],
  getOrganisation => (tradingEntity: TradingEntity) => {
    const organisation = getOrganisation(tradingEntity.organisationId)
    return isNil(organisation) ? true : organisation.enabledFeatures.includes(PlatformFeature.ENABLE_STOCK_BORROWING_LENDING)
  }
)

export const createTradingEntityHasSellSideRelationshipWithFundsSelector = createSelector(
  [createAllEntitiesSelector(EntityType.TRADING_RELATIONSHIP)],
  tradingRelationships => (fundIds: string[], masterLegalAgreementTypes: MasterLegalAgreementType[]) =>
    createTradingEntityHasSellSideRelationshipWithFunds(fundIds, tradingRelationships, false, masterLegalAgreementTypes)
)

export const createTradingEntityHasSellSideUsClearingRelationshipWithFundsSelector = createSelector(
  [createAllEntitiesSelector(EntityType.TRADING_RELATIONSHIP)],
  tradingRelationships => (fundIds: string[], masterLegalAgreementTypes: MasterLegalAgreementType[]) =>
    createTradingEntityHasSellSideRelationshipWithFunds(fundIds, tradingRelationships, true, masterLegalAgreementTypes)
)

export const tradingEntityIsSellSideFilterSelector = createSelector(
  [createEntityGetterSelector(EntityType.ORGANISATION)],
  getOrganisation => (tradingEntity: TradingEntity) => {
    const organisation = getOrganisation(tradingEntity.organisationId)
    return isNotNil(organisation) ? organisation.organisationTypes.includes(OrganisationType.SELL_SIDE) : false
  }
)

export const createTradingEntityBelongsToOrganisation = (organisationId: string) => (tradingEntity: TradingEntity) =>
  tradingEntity.organisationId === organisationId

export const createTradingEntityHasTradingEntityClearingRelationshipGetterSelector = createSelector(
  [createAllEntitiesSelector(EntityType.TRADING_ENTITY_CLEARING), createEntityGetterSelector(EntityType.PRODUCT_SECTOR)],
  (tradingEntityClearings, getProductSector) => (
    clearingHouseId: string,
    collaterals: (Collateral | null)[],
    collateralBaskets: (CollateralBasket | null)[]
  ) => {
    const qualifyingTradingEntityIds = tradingEntityClearings
      .filter(tradingEntityClearing => {
        if (tradingEntityClearing.status !== EntityStatus.ENABLED) {
          return false
        }

        if (tradingEntityClearing.clearingHouseId !== clearingHouseId) {
          return false
        }

        if (collaterals.length !== 0) {
          return collaterals.every(
            collateral =>
              collateral !== null &&
              tradingEntityClearing.productSectorIds !== null &&
              tradingEntityClearing.productSectorIds.includes(collateral.productSectorId)
          )
        } else if (collateralBaskets.length !== 0) {
          return collateralBaskets.every(collateralBasket => {
            const productSector = getProductSector(collateralBasket?.productSectorId)
            const basketType = productSector?.basketType
            return (
              collateralBasket !== null &&
              ((basketType && !doesBasketTypeRequiresTradingEntityPermission(basketType)) ||
                collateralBasket.tradingEntityIds.includes(tradingEntityClearing.tradingEntityId)) &&
              tradingEntityClearing.productSectorIds !== null &&
              tradingEntityClearing.productSectorIds.includes(collateralBasket.productSectorId)
            )
          })
        } else {
          return false
        }
      })
      .map(tradingEntityClearing => tradingEntityClearing.tradingEntityId)

    const uniqueTradingEntityIds = uniq(qualifyingTradingEntityIds)
    return (tradingEntity: TradingEntity) => uniqueTradingEntityIds.includes(tradingEntity.id)
  }
)

export const createTradingEntityDoesNotBelongToFunds = (fundIds: string[]) => (tradingEntity: TradingEntity) =>
  !fundIds.includes(tradingEntity.id)
/**
 * Filter Selectors
 */

export const createFilteredTradingEntitiesSelector = <TProps>(
  filtersSelector: Selector<{}, undefined | Array<(t: TradingEntity) => boolean>, [TProps]>
) =>
  createSelector([createAllEntitiesSelector(EntityType.TRADING_ENTITY), filtersSelector], (allTradingEntities, filters = []) =>
    allTradingEntities.filter(tradingEntity => filters.every(filter => filter(tradingEntity)))
  )
